import { getPlanColor } from "common/elements";
import i18n from "features/shared/i18n";
import { QuotaStatus } from "features/subscription/types";
import { PriceDisplay } from "features/subscription/usePlanPrice";
import { PlanType, PLAN_LIMITS, useSubscription } from "features/subscription";
import {
  PlanCategory,
  BillingPeriod,
} from "features/subscription/useNativePackages";

type Props = {
  isFromUser: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  messageContent: any;
  onPurchase: (category: PlanCategory, billingPeriod: BillingPeriod) => void;
  onOpenPurchasePage: () => void;
  getPriceDisplay?: (
    category: PlanCategory,
    billingPeriod: BillingPeriod,
  ) => PriceDisplay;
};

// メールのトークン（PURCHASE_TOKEN_EXPIRES_DAYS）と揃える。
// メールは切れているのにチャットは押せる、という食い違いを避ける。
const EXPIRE_DAYS = 7;

// 有料プラン加入中は購入させない（重複購入の防止）
const PAID_PLANS: PlanType[] = [
  PlanType.STANDARD,
  PlanType.PREMIUM,
  PlanType.BUILDER,
  PlanType.EDUCATION,
];

const PLAN_OF_CATEGORY: Record<PlanCategory, PlanType> = {
  standard: PlanType.STANDARD,
  premium: PlanType.PREMIUM,
};

const PLAN_NAME_KEY: Record<PlanCategory, string> = {
  standard: "MSG_SUBSCRIPTION_REQUEST_CTA_PLAN_STANDARD",
  premium: "MSG_SUBSCRIPTION_REQUEST_CTA_PLAN_PREMIUM",
};

// kids のプラン比較（PLAN_COMPARISON_SECTIONS）と同じ項目・同じ値を出す。
const toStorage = (bytes: number): string =>
  `${Math.round(bytes / 1024 ** 3)}GB`;

const featuresOf = (category: PlanCategory) => {
  const limits = PLAN_LIMITS[PLAN_OF_CATEGORY[category]];
  const unlimited = i18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_UNLIMITED");
  const value = (quota: QuotaStatus, unitKey: string) =>
    quota.unlimited ? unlimited : i18n.t(unitKey, { count: quota.limit });

  return [
    {
      label: i18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_FEATURE_PROJECTS"),
      value: value(
        limits.projects,
        "MSG_SUBSCRIPTION_REQUEST_CTA_VALUE_PROJECTS",
      ),
      indent: false,
    },
    {
      label: i18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_FEATURE_AI_CHAT"),
      value: value(
        limits.aiChatDaily,
        "MSG_SUBSCRIPTION_REQUEST_CTA_VALUE_TIMES",
      ),
      indent: false,
    },
    {
      label: i18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_FEATURE_AI_IMAGE"),
      value: "",
      indent: false,
    },
    {
      label: i18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_FEATURE_AI_IMAGE_COUNT"),
      value: value(
        limits.aiImageDaily,
        "MSG_SUBSCRIPTION_REQUEST_CTA_VALUE_TIMES",
      ),
      indent: true,
    },
    {
      label: i18n.t(
        "MSG_SUBSCRIPTION_REQUEST_CTA_FEATURE_AI_IMAGE_SIMULTANEOUS",
      ),
      value: value(
        limits.aiImagePerRequest,
        "MSG_SUBSCRIPTION_REQUEST_CTA_VALUE_SHEETS",
      ),
      indent: true,
    },
    {
      label: i18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_FEATURE_STORAGE"),
      value: limits.assets.unlimited
        ? unlimited
        : toStorage(limits.assets.limit),
      indent: false,
    },
  ];
};

// 親子チャットに届く「購入のお願い」CTA カード（保護者視点・常にタップ可能）。
// タップすると Web は Stripe Checkout、アプリは RevenueCat の課金導線を起動する。
export const PurchaseRequestCard = ({
  isFromUser,
  messageContent,
  onPurchase,
  onOpenPurchasePage,
  getPriceDisplay,
}: Props) => {
  const { plan: currentPlan } = useSubscription();

  const hasPlan = !!messageContent?.has_plan;
  const planCategory = (messageContent?.plan_category ??
    null) as PlanCategory | null;
  const billingPeriod = (messageContent?.billing_period ??
    null) as BillingPeriod | null;
  const childName = messageContent?.child_name ?? "";
  const sideMargin = isFromUser ? "ml-1" : "mr-1";

  const requestedAt = messageContent?.requested_at;
  const remainingMs = requestedAt
    ? EXPIRE_DAYS * 24 * 60 * 60 * 1000 -
      (Date.now() - new Date(requestedAt).getTime())
    : null;
  const isExpired = remainingMs !== null && remainingMs <= 0;
  const isAlreadyActive = PAID_PLANS.includes(currentPlan);
  const disabledReason = isAlreadyActive
    ? "ALREADY_ACTIVE"
    : isExpired
      ? "EXPIRED"
      : null;

  const planLabel = planCategory ? i18n.t(PLAN_NAME_KEY[planCategory]) : "";

  // プランごとの内容と購入ボタン。プラン未指定のときは選べるよう2プランとも並べる。
  const planBlock = (category: PlanCategory, periods: BillingPeriod[]) => {
    const color = getPlanColor(PLAN_OF_CATEGORY[category]);
    return (
      <div
        key={category}
        className="flex flex-col gap-2 w-full box-border p-3 rounded-xl border-2 border-solid bg-white"
        style={{ borderColor: color }}
      >
        <p className="text !text-left" style={{ color }}>
          {i18n.t(PLAN_NAME_KEY[category])}
        </p>

        <div className="flex flex-col gap-1 w-full">
          {featuresOf(category).map((feature) => (
            <div
              key={feature.label}
              className={`flex flex-row items-center justify-between w-full ${feature.indent ? "pl-3" : ""}`}
            >
              <span className="text !text-sm text-gray2-100">
                {feature.indent ? `\u21b3 ${feature.label}` : feature.label}
              </span>
              <span className="text !text-sm text-textcolor-black">
                {feature.value}
              </span>
            </div>
          ))}
        </div>

        <div className="flex flex-row gap-2 w-full">
          {periods.map((period) => {
            const price = getPriceDisplay?.(category, period);
            const isAnnual = period === "annual";
            const showDeal = isAnnual && periods.length > 1;
            return (
              <div key={period} className="flex flex-col flex-1 gap-1">
                {/* バッジの有無でボタンの位置がずれないよう、無い側も同じ要素を透明で置く */}
                <p className="text !text-xs text-center">
                  <span
                    className="inline-block px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: showDeal ? "#ffa03b" : "transparent",
                      color: showDeal ? "#ffffff" : "transparent",
                    }}
                  >
                    {i18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_ANNUAL_DEAL")}
                  </span>
                </p>
                <button
                  type="button"
                  disabled={!!disabledReason}
                  onClick={() => onPurchase(category, period)}
                  className={`flex flex-row items-center justify-center gap-1 border-none box-border rounded-xl py-2 ${
                    disabledReason
                      ? "self-center px-4 cursor-not-allowed"
                      : "w-full px-2 cursor-pointer"
                  }`}
                  style={{
                    backgroundColor: disabledReason ? "#c8ccd4" : color,
                  }}
                >
                  <span className="text !text-sm text-white whitespace-nowrap">
                    {disabledReason
                      ? i18n.t(
                          `MSG_SUBSCRIPTION_REQUEST_CTA_${disabledReason}_BTN`,
                        )
                      : i18n.t(
                          isAnnual
                            ? "MSG_SUBSCRIPTION_REQUEST_CTA_ANNUAL"
                            : "MSG_SUBSCRIPTION_REQUEST_CTA_MONTHLY",
                          { price: price?.priceString ?? "" },
                        )}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`flex flex-col gap-3 w-[75%] p-4 rounded-2xl bg-white border-2 border-solid border-blue-100 ${sideMargin}`}
    >
      <p className="text text-textcolor-black !text-left">
        {i18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_TITLE")}
      </p>
      <p className="text text-textcolor-black !text-left break-all !whitespace-pre-line">
        {hasPlan
          ? i18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_BODY_WITH_PLAN", {
              name: childName,
              plan: planLabel,
            })
          : i18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_BODY_NO_PLAN", {
              name: childName,
            })}
      </p>

      {hasPlan && planCategory && billingPeriod
        ? planBlock(planCategory, [billingPeriod])
        : (["standard", "premium"] as PlanCategory[]).map((category) =>
            planBlock(category, ["monthly", "annual"]),
          )}

      <p className="text !text-xs text-gray2-100 !text-left">
        {i18n.t(
          disabledReason
            ? `MSG_SUBSCRIPTION_REQUEST_CTA_${disabledReason}`
            : "MSG_SUBSCRIPTION_REQUEST_CTA_SAFETY_NOTE",
        )}
      </p>

      {!disabledReason && remainingMs !== null && (
        <p className="text !text-xs text-gray2-100 !text-left">
          {remainingMs > 24 * 60 * 60 * 1000
            ? i18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_REMAINING_DAYS", {
                days: Math.ceil(remainingMs / (24 * 60 * 60 * 1000)),
              })
            : i18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_REMAINING_TODAY")}
        </p>
      )}

      {!hasPlan && (
        <button
          type="button"
          onClick={onOpenPurchasePage}
          className="text !text-xs text-blue-100 bg-transparent border-none cursor-pointer underline"
        >
          {i18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_VIEW_PLANS_BTN")}
        </button>
      )}
    </div>
  );
};
